import { useState } from 'react';
import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  ConfirmDialog,
  EmptyState,
  HeroActions,
  HeroBar,
  HeroBody,
  HeroMeta,
  HeroTitle,
  IconButton,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  PageBody,
  Spinner,
  Tab,
  TabList,
} from '@wundo/ui';
import { ApiError } from '@wundo/client/api.ts';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { PageHeader } from '@/components/PageHeader.tsx';
import { useRecordOrganisation } from '@/lib/recordOrganisation.ts';
import { CohortDetailsTab } from '@/components/cohorts/CohortDetailsTab.tsx';
import { CohortDocumentsTab } from '@/components/cohorts/CohortDocumentsTab.tsx';
import { CohortParticipantsTab } from '@/components/cohorts/CohortParticipantsTab.tsx';
import { CohortPromptTab } from '@/components/cohorts/CohortPromptTab.tsx';
import { type HashTab, useHashTabs } from '@/lib/useHashTabs.ts';
import { deleteCohort, fetchCohort } from '@/lib/cohorts.ts';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';
import { Clock, EllipsisVertical, Pencil, Trash2 } from 'lucide-react';

export const Route = createFileRoute('/_app/cohorts/$id/')({
  staticData: { orgScope: 'record', orgScopeList: '/cohorts' },
  component: CohortDetailRoute,
});

function CohortDetailRoute() {
  return <CohortDetailPage id={Route.useParams().id} />;
}

export function CohortDetailPage({ id }: { id: string }) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [error, setError] = useState('');

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });
  const superadmin = user?.role === 'superadmin';

  const {
    data: cohort,
    isPending,
    error: loadError,
  } = useQuery({
    queryKey: ['cohorts', 'detail', id],
    queryFn: () => fetchCohort(id),
    retry: false,
  });
  useRecordOrganisation(cohort?.organisation_id);

  /* The prompt is a superadmin's business, so the tab is only there for one. */
  const tabs: HashTab[] = [
    { key: 'details', label: 'Details' },
    { key: 'participants', label: 'Participants' },
    { key: 'documents', label: 'Documents' },
    ...(superadmin ? [{ key: 'prompt', label: 'Prompt' }] : []),
  ];
  const { active, select } = useHashTabs(tabs);

  const remove = useMutation({
    mutationFn: () => deleteCohort(id),
    onSuccess: async () => {
      /* The list, not this cohort: refetching what was just deleted asks for a 404. */
      queryClient.removeQueries({ queryKey: ['cohorts', 'detail', id] });
      await queryClient.invalidateQueries({ queryKey: ['cohorts', 'list'] });
      await router.navigate({ to: '/cohorts' });
    },
    onError: (failure) => setError(apiErrorMessage(failure, 'Failed to delete cohort')),
  });

  if (isPending) {
    return (
      <PageBody className='flex justify-center'>
        <Spinner />
      </PageBody>
    );
  }

  if (loadError) {
    /* Not found is a state the page can draw; anything else is the shell's. */
    if (!(loadError instanceof ApiError) || loadError.status !== 404) throw loadError;

    return (
      <>
        <PageHeader title='Cohort' />
        <PageBody>
          <Card>
            <EmptyState
              title='Cohort not found'
              description='It may have been deleted.'
            />
          </Card>
        </PageBody>
      </>
    );
  }

  return (
    <PageBody flush className='flex flex-col gap-5'>
      <HeroBar>
        <HeroBody>
          <HeroTitle>{cohort.title}</HeroTitle>
          <HeroMeta>
            {cohort.track ? (
              <span>
                Part of{' '}
                <Link
                  to='/tracks/$id'
                  params={{ id: cohort.track.id }}
                  className='underline underline-offset-2 hover:text-white'
                >
                  {cohort.track.title}
                </Link>
              </span>
            ) : (
              <span>No track</span>
            )}
          </HeroMeta>
        </HeroBody>
        <HeroActions>
          <CohortActionsMenu
            cohortId={cohort.id}
            superadmin={superadmin}
            onDelete={() => setConfirmingDelete(true)}
          />
        </HeroActions>
      </HeroBar>

      <TabList>
        {tabs.map((tab) => (
          <Tab key={tab.key} active={active === tab.key} onClick={() => select(tab.key)}>
            {tab.label}
          </Tab>
        ))}
      </TabList>

      {active === 'details' && <CohortDetailsTab cohort={cohort} />}
      {active === 'participants' && (
        <CohortParticipantsTab
          cohortId={cohort.id}
          organisationId={cohort.organisation_id}
        />
      )}
      {active === 'documents' && (
        <CohortDocumentsTab
          cohortId={cohort.id}
          trackId={cohort.track_id}
          organisationId={cohort.organisation_id}
        />
      )}
      {active === 'prompt' && superadmin && <CohortPromptTab cohortId={cohort.id} />}

      <ConfirmDialog
        open={confirmingDelete}
        onOpenChange={setConfirmingDelete}
        title='Delete cohort?'
        description={
          <>
            Are you sure you want to delete cohort <strong>{cohort.title}</strong>? This
            action cannot be undone.
          </>
        }
        confirmLabel='Delete'
        cancelLabel='Cancel'
        variant='danger'
        error={error}
        onConfirm={async () => {
          setError('');
          await remove.mutateAsync().catch(() => undefined);
        }}
      />
    </PageBody>
  );
}

interface CohortActionsMenuProps {
  cohortId: string;
  superadmin: boolean;
  onDelete: () => void;
}

function CohortActionsMenu({ cohortId, superadmin, onDelete }: CohortActionsMenuProps) {
  const router = useRouter();

  return (
    <Menu>
      <MenuTrigger
        render={<IconButton aria-label='Cohort actions' size='sm' variant='surface' />}
      >
        <EllipsisVertical className='size-4' aria-hidden='true' />
      </MenuTrigger>
      <MenuContent align='end'>
        <MenuItem
          onClick={() =>
            void router.navigate({ to: '/cohorts/$id/edit', params: { id: cohortId } })
          }
        >
          <Pencil aria-hidden='true' />
          Edit
        </MenuItem>
        {superadmin && (
          <MenuItem
            onClick={() =>
              void router.navigate({
                href: `/admin/change-logs?entity_type=cohort&entity_id=${cohortId}`,
              })
            }
          >
            <Clock aria-hidden='true' />
            Change Logs
          </MenuItem>
        )}
        <MenuItem tone='danger' onClick={onDelete}>
          <Trash2 aria-hidden='true' />
          Delete
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}
